import { useState, useEffect, useCallback } from 'react';
import { phoneNumbersAPI, portingAPI, agentsAPI, billingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Phone, Search, ShoppingCart, Trash2, Loader2, Star,
  MapPin, CheckCircle2, AlertCircle, ArrowRightLeft, Save, X, Clock,
  Settings2, PhoneForwarded, Bot, Globe, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const COUNTRIES = [
  { code: 'IN', name: 'India', flag: '🇮🇳' },
];

export default function PhoneNumbersPage() {
  const [tab, setTab] = useState('my-numbers');
  const [myNumbers, setMyNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [userPlan, setUserPlan] = useState('free');

  // Search state
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [country, setCountry] = useState('IN');
  const [pattern, setPattern] = useState('');
  const [purchasing, setPurchasing] = useState('');
  const [markupInfo, setMarkupInfo] = useState({ monthly_percent: 40, setup_percent: 100 });

  // Inbound settings modal state
  const [inboundSettingsFor, setInboundSettingsFor] = useState(null); // number object
  const [inboundMode, setInboundMode] = useState('ai');
  const [inboundForwardTo, setInboundForwardTo] = useState('');
  const [inboundAgentId, setInboundAgentId] = useState('');
  const [savingInbound, setSavingInbound] = useState(false);

  // Porting state
  const [portingRequests, setPortingRequests] = useState([]);
  const [showPortForm, setShowPortForm] = useState(false);
  const [portForm, setPortForm] = useState({
    phone_number: '', current_carrier: '', account_number: '',
    pin_passcode: '', business_name: '', authorized_person: '', notes: ''
  });
  const [portingSaving, setPortingSaving] = useState(false);

  const loadMyNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await phoneNumbersAPI.list();
      setMyNumbers(res.data.numbers || []);
    } catch { toast.error('Failed to load numbers'); }
    setLoading(false);
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const res = await agentsAPI.list();
      setAgents(res.data.agents || res.data || []);
    } catch {}
  }, []);

  const loadUserPlan = useCallback(async () => {
    try {
      const res = await billingAPI.usage();
      setUserPlan(res.data?.plan?.id || 'free');
    } catch {}
  }, []);

  const loadPortingRequests = useCallback(async () => {
    try {
      const res = await portingAPI.list();
      setPortingRequests(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { loadMyNumbers(); loadPortingRequests(); loadAgents(); loadUserPlan(); }, [loadMyNumbers, loadPortingRequests, loadAgents, loadUserPlan]);

  const handleSearch = async () => {
    setSearching(true);
    setSearchResults([]);
    try {
      const res = await phoneNumbersAPI.search({
        country, pattern, per_page: 30
      });
      setSearchResults(res.data.numbers || []);
      setMarkupInfo(res.data.markup_info || { monthly_percent: 40, setup_percent: 100 });
      if (res.data.numbers?.length === 0) toast.info('No numbers matched. Try clearing the pattern filter.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    }
    setSearching(false);
  };

  const handlePurchase = async (number) => {
    if (!window.confirm(
      `Buy ${number.phone_number}?\n\n` +
      `One-time setup: ₹${number.setup_fee}\n` +
      `Monthly rental: ₹${number.monthly_fee}\n` +
      `Total now: ₹${(number.setup_fee + number.monthly_fee).toFixed(2)}\n\n` +
      `(Deducted from your wallet.)`
    )) return;
    setPurchasing(number.phone_number);
    try {
      await phoneNumbersAPI.purchase({
        phone_number: number.phone_number,
        country: number.country,
      });
      toast.success(`Purchased ${number.phone_number}! Check "My Numbers" to configure inbound.`);
      setSearchResults(prev => prev.filter(n => n.phone_number !== number.phone_number));
      loadMyNumbers();
      setTab('my-numbers');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    }
    setPurchasing('');
  };

  const handleSetDefault = async (id) => {
    try {
      await phoneNumbersAPI.setDefault(id);
      toast.success('Default number updated');
      loadMyNumbers();
    } catch { toast.error('Failed to set default'); }
  };

  const handleRelease = async (id, phone) => {
    if (!window.confirm(`Release ${phone}? This removes it from your account and stops monthly renewals.`)) return;
    try {
      await phoneNumbersAPI.release(id);
      toast.success('Number released');
      loadMyNumbers();
    } catch { toast.error('Failed to release number'); }
  };

  const openInboundSettings = (num) => {
    setInboundSettingsFor(num);
    setInboundMode(num.inbound_mode || 'ai');
    setInboundForwardTo(num.inbound_forward_to || '');
    setInboundAgentId(num.inbound_agent_id || '');
  };

  const closeInboundSettings = () => {
    setInboundSettingsFor(null);
    setInboundMode('ai');
    setInboundForwardTo('');
    setInboundAgentId('');
  };

  const handleSaveInbound = async () => {
    if (!inboundSettingsFor) return;
    if (inboundMode === 'forward' && !inboundForwardTo.trim()) {
      toast.error('Forwarding number is required');
      return;
    }
    if (inboundMode === 'ai' && !inboundAgentId) {
      toast.error('Please select an AI agent');
      return;
    }
    setSavingInbound(true);
    try {
      await phoneNumbersAPI.updateInbound(inboundSettingsFor.id, {
        inbound_mode: inboundMode,
        inbound_forward_to: inboundForwardTo,
        inbound_agent_id: inboundAgentId,
      });
      toast.success('Inbound settings saved');
      closeInboundSettings();
      loadMyNumbers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save inbound settings');
    }
    setSavingInbound(false);
  };

  const handlePortSubmit = async () => {
    if (!portForm.phone_number || !portForm.current_carrier) {
      toast.error('Phone number and current carrier are required');
      return;
    }
    setPortingSaving(true);
    try {
      await portingAPI.create(portForm);
      toast.success('Porting request submitted');
      setShowPortForm(false);
      setPortForm({ phone_number: '', current_carrier: '', account_number: '', pin_passcode: '', business_name: '', authorized_person: '', notes: '' });
      loadPortingRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit porting request');
    }
    setPortingSaving(false);
  };

  const handleCancelPort = async (id) => {
    if (!window.confirm('Cancel this porting request?')) return;
    try {
      await portingAPI.cancel(id);
      toast.success('Porting request cancelled');
      loadPortingRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const portStatusColors = {
    submitted: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
    in_review: 'bg-amber-600/20 text-amber-400 border-amber-600/30',
    porting: 'bg-amber-600/20 text-amber-400 border-amber-600/30',
    active: 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30',
    rejected: 'bg-red-600/20 text-red-400 border-red-600/30',
    cancelled: 'bg-slate-600/20 text-slate-400 border-slate-600/30',
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="phone-numbers-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] text-white">Phone Numbers</h1>
        <p className="text-slate-400 mt-1">Manage your outbound caller IDs. Purchase dedicated numbers or use the platform default.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900/50 rounded-xl p-1 border border-white/5 w-fit" data-testid="phone-tabs">
        <button onClick={() => setTab('my-numbers')} data-testid="tab-my-numbers"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            tab === 'my-numbers' ? 'bg-amber-600/15 text-amber-300' : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}>
          <Phone className="w-4 h-4" /> My Numbers
          {myNumbers.length > 0 && <Badge className="bg-amber-600/20 text-amber-300 border-amber-600/30 text-xs ml-1">{myNumbers.length}</Badge>}
        </button>
        <button onClick={() => setTab('buy')} data-testid="tab-buy-number"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            tab === 'buy' ? 'bg-amber-600/15 text-amber-300' : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}>
          <ShoppingCart className="w-4 h-4" /> Buy Number
        </button>
        <button onClick={() => setTab('port')} data-testid="tab-port-number"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            tab === 'port' ? 'bg-amber-600/15 text-amber-300' : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}>
          <ArrowRightLeft className="w-4 h-4" /> Port Number
          {portingRequests.length > 0 && <Badge className="bg-amber-600/20 text-amber-300 border-amber-600/30 text-xs ml-1">{portingRequests.length}</Badge>}
        </button>
      </div>

      {/* My Numbers Tab */}
      {tab === 'my-numbers' && (
        <div className="space-y-4" data-testid="my-numbers-tab">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>
          ) : myNumbers.length === 0 ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <Phone className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">No numbers yet</h3>
              <p className="text-slate-400 mb-6 text-sm">Buy an Indian DID to run campaigns and receive inbound calls.</p>
              {userPlan !== 'free' && (
                <Button onClick={() => setTab('buy')} className="bg-amber-600 hover:bg-amber-700 text-white">
                  <ShoppingCart className="w-4 h-4 mr-2" /> Browse Numbers
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {myNumbers.map((num, i) => {
                const isPlatform = num.is_platform === true;
                return (
                <div key={num.id} className={`glass-card rounded-xl p-4 animate-fade-in ${isPlatform ? 'border border-amber-600/20' : ''}`} style={{ animationDelay: `${i * 50}ms` }}
                  data-testid={`number-card-${num.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPlatform ? 'bg-amber-600/20' : num.is_default ? 'bg-amber-600/20' : 'bg-slate-700/50'}`}>
                        {isPlatform ? <Globe className="w-5 h-5 text-amber-400" /> : <Phone className={`w-5 h-5 ${num.is_default ? 'text-amber-400' : 'text-slate-400'}`} />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium font-mono">{num.phone_number}</span>
                          {isPlatform && (
                            <Badge className="bg-amber-600/20 text-amber-300 border-amber-600/30 text-xs">
                              <Shield className="w-3 h-3 mr-1" /> Platform Default
                            </Badge>
                          )}
                          {isPlatform && (
                            <Badge className="bg-blue-600/20 text-blue-300 border-blue-600/30 text-xs">
                              Outbound Only
                            </Badge>
                          )}
                          {!isPlatform && num.is_default && (
                            <Badge className="bg-amber-600/20 text-amber-300 border-amber-600/30 text-xs" data-testid={`default-badge-${num.id}`}>
                              <Star className="w-3 h-3 mr-1 fill-amber-400" /> Default
                            </Badge>
                          )}
                          {!isPlatform && num.inbound_mode === 'forward' && (
                            <Badge className="bg-blue-600/20 text-blue-300 border-blue-600/30 text-xs">
                              <PhoneForwarded className="w-3 h-3 mr-1" /> Forward: {num.inbound_forward_to}
                            </Badge>
                          )}
                          {!isPlatform && num.inbound_mode === 'ai' && num.inbound_agent_id && (
                            <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-600/30 text-xs">
                              <Bot className="w-3 h-3 mr-1" /> AI Agent
                            </Badge>
                          )}
                          {!isPlatform && num.inbound_mode === 'ai' && !num.inbound_agent_id && (
                            <Badge className="bg-red-600/20 text-red-300 border-red-600/30 text-xs">Inbound not configured</Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                          {isPlatform ? (
                            <span className="text-amber-400/60">Shared number for all users · Free · No inbound</span>
                          ) : (
                            <>
                              {num.locality && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{num.locality}</span>}
                              {num.region && <span>{num.region}</span>}
                              <span>₹{num.retail_monthly || num.monthly_fee || 0}/mo</span>
                              <span>Purchased {new Date(num.purchased_at).toLocaleDateString('en-IN')}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Actions — hide for platform number */}
                    {!isPlatform && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openInboundSettings(num)}
                          data-testid={`inbound-settings-${num.id}`}
                          className="text-slate-400 hover:text-blue-300 hover:bg-blue-600/10 text-xs">
                          <Settings2 className="w-3.5 h-3.5 mr-1" /> Inbound
                        </Button>
                        {!num.is_default && (
                          <Button size="sm" variant="ghost" onClick={() => handleSetDefault(num.id)}
                            data-testid={`set-default-${num.id}`}
                            className="text-slate-400 hover:text-amber-300 hover:bg-amber-600/10 text-xs">
                            <Star className="w-3.5 h-3.5 mr-1" /> Default
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleRelease(num.id, num.phone_number)}
                          data-testid={`release-${num.id}`}
                          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Buy Number Tab */}
      {tab === 'buy' && (
        <div className="space-y-5" data-testid="buy-number-tab">
          {userPlan === 'free' ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <Shield className="w-12 h-12 text-amber-500/40 mx-auto mb-4" />
              <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">Upgrade to Buy Numbers</h3>
              <p className="text-slate-400 mb-4 text-sm">Free plan users use the shared DotCall platform number for outbound calls.</p>
              <p className="text-slate-500 text-xs mb-6">Upgrade to Standard or above to purchase your own dedicated Indian DID for both inbound and outbound calls.</p>
              <Button onClick={() => window.location.href = '/pricing'} className="bg-amber-600 hover:bg-amber-700 text-white">
                View Plans & Upgrade
              </Button>
            </div>
          ) : (
          <>
          <div className="glass-card rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Browse Available Indian DIDs</h3>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-44">
                <label className="text-xs text-slate-400 mb-1 block">Country</label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    {COUNTRIES.map(c => (
                      <SelectItem key={c.code} value={c.code} className="text-white hover:bg-white/5">
                        {c.flag} {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-slate-400 mb-1 block">Filter by digits (optional)</label>
                <Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="e.g. 9999 or 140"
                  data-testid="pattern-input"
                  className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
              </div>
              <Button onClick={handleSearch} disabled={searching} data-testid="search-numbers-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white h-10 px-5">
                {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                Search
              </Button>
            </div>
          </div>

          {/* Search Results */}
          {searching ? (
            <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2" data-testid="search-results">
              <p className="text-sm text-slate-400">{searchResults.length} numbers available</p>
              {searchResults.map((num, i) => (
                <div key={num.phone_number} className="glass-card rounded-xl p-4 flex items-center justify-between animate-fade-in" style={{ animationDelay: `${i * 40}ms` }}
                  data-testid={`result-${num.phone_number}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-600/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <div className="text-white font-medium font-mono">{num.phone_number}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                        {num.locality && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{num.locality}</span>}
                        {num.region && <span>{num.region}</span>}
                        <span className="capitalize">{num.number_type || 'local'}</span>
                        {num.capabilities?.voice && <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/30 text-[10px]">Voice</Badge>}
                        {num.capabilities?.sms && <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30 text-[10px]">SMS</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-white font-semibold text-sm">₹{num.monthly_fee}/mo</div>
                      <div className="text-[10px] text-slate-500">+ ₹{num.setup_fee} setup</div>
                    </div>
                    <Button size="sm" onClick={() => handlePurchase(num)}
                      disabled={purchasing === num.phone_number}
                      data-testid={`buy-${num.phone_number}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4">
                      {purchasing === num.phone_number ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShoppingCart className="w-4 h-4 mr-1" />}
                      {purchasing === num.phone_number ? 'Buying…' : 'Buy'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* Info Box */}
          <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-300/80">
              <p className="font-medium text-blue-300 mb-1">How Phone Numbers Work</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Your number powers both outbound campaigns and inbound calls</li>
                <li>Configure inbound to forward to your personal number, or let the AI agent answer</li>
                <li>Setup + first month are charged now; subsequent months are auto-debited</li>
                <li>Set a number as "Default" to auto-pick it as caller ID for all campaigns</li>
              </ul>
            </div>
          </div>
          </>
          )}
        </div>
      )}

      {/* Inbound Settings Modal */}
      {inboundSettingsFor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeInboundSettings} data-testid="inbound-settings-modal">
          <div className="glass-card rounded-2xl p-6 max-w-lg w-full space-y-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-white">Inbound Settings</h3>
                <p className="text-xs text-slate-400 mt-0.5 font-mono">{inboundSettingsFor.phone_number}</p>
              </div>
              <button onClick={closeInboundSettings} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Toggle */}
            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-white flex items-center gap-2">
                    <PhoneForwarded className="w-4 h-4 text-blue-400" /> Forward inbound calls to my personal number
                  </div>
                  <div className="text-xs text-slate-500 mt-1">When ON, incoming calls ring your mobile directly. When OFF, AI agent answers.</div>
                </div>
                <Switch checked={inboundMode === 'forward'}
                  onCheckedChange={(v) => setInboundMode(v ? 'forward' : 'ai')}
                  data-testid="inbound-forward-toggle" />
              </div>
            </div>

            {inboundMode === 'forward' ? (
              <div className="space-y-2">
                <label className="text-xs text-slate-400 block">Your personal number</label>
                <Input value={inboundForwardTo} onChange={(e) => setInboundForwardTo(e.target.value)}
                  placeholder="+91 98765 43210" data-testid="forward-to-input"
                  className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 font-mono" />
                <p className="text-[11px] text-slate-500">Enter with +91 country code. Inbound minutes are billed per your plan.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-xs text-slate-400 block">AI Agent to answer inbound calls</label>
                <Select value={inboundAgentId} onValueChange={setInboundAgentId}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="inbound-agent-select">
                    <SelectValue placeholder="Select an agent" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    {agents.length === 0 ? (
                      <div className="px-2 py-3 text-xs text-slate-500 text-center">No agents yet — create one in Agents page</div>
                    ) : agents.map(a => (
                      <SelectItem key={a.id} value={a.id} className="text-white hover:bg-white/5">
                        {a.name} · {a.language} · {a.voice}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">
                  Flow: caller dials this number → ringtone plays while we pre-connect Grok →
                  after pickup, agent instantly greets and holds a live AI conversation. Transcript & recording saved.
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button onClick={handleSaveInbound} disabled={savingInbound} data-testid="save-inbound-btn"
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">
                {savingInbound ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
              <Button variant="ghost" onClick={closeInboundSettings} className="text-slate-400 hover:text-white">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Port Number Tab */}
      {tab === 'port' && (
        <div className="space-y-5" data-testid="port-number-tab">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white font-['Outfit']">Port Your Number</h3>
              <p className="text-sm text-slate-400 mt-0.5">Transfer your existing business number to DotCall</p>
            </div>
            {!showPortForm && (
              <Button onClick={() => setShowPortForm(true)} data-testid="start-porting-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white text-sm">
                <ArrowRightLeft className="w-4 h-4 mr-2" /> Port a Number
              </Button>
            )}
          </div>

          {showPortForm && (
            <div className="glass-card rounded-xl p-5 space-y-4 animate-fade-in" data-testid="porting-form">
              <h4 className="text-sm font-medium text-white">Porting Request Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Phone Number to Port *</label>
                  <Input value={portForm.phone_number} onChange={(e) => setPortForm(p => ({ ...p, phone_number: e.target.value }))}
                    placeholder="+1234567890" data-testid="port-phone-input"
                    className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Current Carrier *</label>
                  <Input value={portForm.current_carrier} onChange={(e) => setPortForm(p => ({ ...p, current_carrier: e.target.value }))}
                    placeholder="e.g. AT&T, Verizon, Airtel, Jio" data-testid="port-carrier-input"
                    className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Account Number</label>
                  <Input value={portForm.account_number} onChange={(e) => setPortForm(p => ({ ...p, account_number: e.target.value }))}
                    placeholder="Carrier account number" data-testid="port-account-input"
                    className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">PIN / Passcode</label>
                  <Input value={portForm.pin_passcode} onChange={(e) => setPortForm(p => ({ ...p, pin_passcode: e.target.value }))}
                    placeholder="Carrier PIN or passcode" data-testid="port-pin-input"
                    className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Business Name</label>
                  <Input value={portForm.business_name} onChange={(e) => setPortForm(p => ({ ...p, business_name: e.target.value }))}
                    placeholder="Registered business name" data-testid="port-business-input"
                    className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Authorized Person</label>
                  <Input value={portForm.authorized_person} onChange={(e) => setPortForm(p => ({ ...p, authorized_person: e.target.value }))}
                    placeholder="Name on the account" data-testid="port-person-input"
                    className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Additional Notes</label>
                <Input value={portForm.notes} onChange={(e) => setPortForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any additional information..." data-testid="port-notes-input"
                  className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handlePortSubmit} disabled={portingSaving} data-testid="submit-porting-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white text-sm">
                  {portingSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Submit Request
                </Button>
                <Button variant="ghost" onClick={() => setShowPortForm(false)} className="text-slate-400 hover:text-white text-sm">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Porting Requests List */}
          {portingRequests.length === 0 && !showPortForm ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <ArrowRightLeft className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">No porting requests</h3>
              <p className="text-slate-400 text-sm mb-6">Transfer your existing phone number to DotCall to use as your outbound caller ID</p>
              <Button onClick={() => setShowPortForm(true)} className="bg-amber-600 hover:bg-amber-700 text-white">
                <ArrowRightLeft className="w-4 h-4 mr-2" /> Port a Number
              </Button>
            </div>
          ) : portingRequests.length > 0 && (
            <div className="space-y-3">
              {portingRequests.map((req, i) => (
                <div key={req.id} className="glass-card rounded-xl p-4 animate-fade-in" style={{ animationDelay: `${i * 50}ms` }} data-testid={`porting-card-${req.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-600/10 flex items-center justify-center">
                        <ArrowRightLeft className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium font-mono">{req.phone_number}</span>
                          <Badge className={`text-xs ${portStatusColors[req.status] || portStatusColors.submitted}`}>
                            {req.status === 'in_review' ? 'In Review' : req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          From: {req.current_carrier} | Submitted {new Date(req.created_at).toLocaleDateString('en-IN')}
                          {req.business_name && ` | ${req.business_name}`}
                        </div>
                      </div>
                    </div>
                    {(req.status === 'submitted' || req.status === 'in_review') && (
                      <Button size="sm" variant="ghost" onClick={() => handleCancelPort(req.id)}
                        data-testid={`cancel-port-${req.id}`}
                        className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 text-xs">
                        Cancel
                      </Button>
                    )}
                  </div>
                  {req.admin_notes && (
                    <div className="mt-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/10 text-xs text-blue-300">
                      Admin Note: {req.admin_notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Info */}
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-300/80">
              <p className="font-medium text-amber-300 mb-1">How Number Porting Works</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Submit your number details — we'll handle the transfer with your current carrier</li>
                <li>Porting typically takes 1-3 business days for mobile, 2-4 weeks for landlines</li>
                <li>Your number stays active during the porting process — no downtime</li>
                <li>Once complete, the number appears in "My Numbers" and can be set as default</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
